/**
 * Synthetic "voice of the consumer" behind every KPI: response distributions
 * consistent with the predicted value, and templated verbatims that reference
 * the selected launch options. Everything is deterministic for a given
 * (KPI, audience, configuration) so the screen is stable while exploring.
 */

import { KPIS } from "./model";
import { quotePools } from "./quotes";
import { STATES } from "./regions";
import {
  AudienceId,
  Configuration,
  KpiDefinition,
  KpiId,
  Kpis,
  Sentiment,
  Verbatim,
} from "./types";

/* ── Types ─────────────────────────────────────── */

export interface DistributionBin {
  label: string;
  /** Share of respondents, 0–100. */
  share: number;
}

/* ── Constants ─────────────────────────────────── */

/** Simulated respondents per audience. */
export const SAMPLE_SIZE = 600;

const AGE_RANGES: Record<AudienceId, [number, number]> = {
  all: [18, 65],
  genz: [18, 27],
  millennials: [28, 43],
  nutellaBuyers: [22, 58],
  parents: [29, 47],
  pbHeavy: [20, 62],
};

const SCALE_LABELS: Record<KpiId, string[]> = {
  appeal: [
    "Very appealing",
    "Appealing",
    "Neutral",
    "Unappealing",
    "Not at all appealing",
  ],
  awareness: [
    "Would definitely notice",
    "Would probably notice",
    "Not sure",
    "Probably not",
    "Definitely not",
  ],
  brandFit: [
    "Fits Nutella perfectly",
    "Fits well",
    "Neutral",
    "Doesn't quite fit",
    "Doesn't fit at all",
  ],
  purchaseIntent: [
    "Definitely would buy",
    "Probably would buy",
    "Might or might not",
    "Probably not",
    "Definitely not",
  ],
  trial: [
    "Will try at launch",
    "Will try eventually",
    "Might try",
    "Unlikely to try",
    "Won't try",
  ],
  wtp: [],
};

const WTP_PRICES = [3.99, 4.49, 4.99, 5.49, 5.99, 6.49, 6.99];

/* ── Distributions ──────────────────────────────── */

/** Response distribution consistent with the predicted KPI value. */
export const distribution = (
  kpi: KpiDefinition,
  value: number
): DistributionBin[] => {
  if (kpi.format === "currency") {
    return gaussianBins(
      WTP_PRICES.map((p) => `$${p.toFixed(2)}`),
      WTP_PRICES,
      value,
      0.62
    );
  }
  if (kpi.format === "score") {
    // 0–100 index → mean on a 1–10 liking scale, five 2-point bins.
    return gaussianBins(
      SCALE_LABELS[kpi.id],
      [9.5, 7.5, 5.5, 3.5, 1.5],
      value / 10,
      2.1
    );
  }
  // Percent KPIs are top-2 box shares on a 5-point scale.
  const top2 = Math.max(2, Math.min(96, value));
  const rest = 100 - top2;
  const shares = [
    top2 * 0.38,
    top2 * 0.62,
    rest * 0.42,
    rest * 0.36,
    rest * 0.22,
  ];
  return SCALE_LABELS[kpi.id].map((label, i) => ({ label, share: shares[i] }));
};

/** Share of respondents whose acceptable price is at or above `price`. */
export const shareAcceptingPrice = (wtp: number, price: number): number =>
  distribution(KPIS.find((k) => k.id === "wtp") ?? KPIS[1], wtp)
    .filter((_, i) => WTP_PRICES[i] >= price - 0.001)
    .reduce((sum, bin) => sum + bin.share, 0);

/* ── Verbatims ──────────────────────────────────── */

/** Deterministic sample of simulated respondents talking about one KPI. */
export const verbatims = (
  kpi: KpiId,
  configuration: Configuration,
  audience: AudienceId,
  kpis: Kpis,
  count: number
): Verbatim[] => {
  const random = rng(
    hash(`${kpi}|${audience}|${JSON.stringify(configuration)}`)
  );
  const definition = KPIS.find((k) => k.id === kpi) ?? KPIS[0];
  const bins = distribution(definition, kpis[kpi]);
  const pools = quotePools(kpi, configuration, audience);
  const used = new Set<string>();
  const [minAge, maxAge] = AGE_RANGES[audience];
  const result: Verbatim[] = [];

  for (let i = 0; i < count; i++) {
    const rating = drawRating(bins, random, definition);
    const sentiment: Sentiment =
      rating >= 4 ? "positive" : rating === 3 ? "neutral" : "negative";
    const pool = pools[sentiment].filter((q) => !used.has(q));
    const quote =
      pool.length > 0
        ? pool[Math.floor(random() * pool.length)]
        : pools[sentiment][Math.floor(random() * pools[sentiment].length)];
    used.add(quote);
    const state = STATES[Math.floor(random() * STATES.length)];
    result.push({
      age: minAge + Math.floor(random() * (maxAge - minAge + 1)),
      audience,
      gender: random() < 0.55 ? "Female" : "Male",
      quote,
      rating,
      sentiment,
      state: state.name,
    });
  }
  // Ratings are drawn from the distribution, so order is already random.
  return result;
};

/* ── Private helpers ────────────────────────────── */

/** Rating 1–5 (5 = most favourable) drawn from the bins; WTP maps to price. */
const drawRating = (
  bins: DistributionBin[],
  random: () => number,
  definition: KpiDefinition
): number => {
  let r = random() * 100;
  let index = 0;
  for (let i = 0; i < bins.length; i++) {
    r -= bins[i].share;
    if (r <= 0) {
      index = i;
      break;
    }
    index = i;
  }
  if (definition.format === "currency") {
    // Higher acceptable price → more favourable.
    return Math.max(1, Math.min(5, Math.round(1 + (index / 6) * 4)));
  }
  return 5 - index;
};

const gaussianBins = (
  labels: string[],
  centers: number[],
  mean: number,
  sd: number
): DistributionBin[] => {
  const weights = centers.map((c) =>
    Math.exp(-((c - mean) ** 2) / (2 * sd * sd))
  );
  const total = weights.reduce((a, b) => a + b, 0);
  return labels.map((label, i) => ({
    label,
    share: (weights[i] / total) * 100,
  }));
};

const hash = (input: string): number => {
  let h = 2166136261;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
};

/** mulberry32 PRNG — small, deterministic. */
const rng = (seed: number) => {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};
